import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';

import { TicketCreateModal } from './TicketCreateModal.jsx';
import { TicketDetailModal } from './TicketDetailModal.jsx';

const STATUS_VARIANT = { OPEN: 'warning', IN_PROGRESS: 'info', RESOLVED: 'success', CLOSED: 'neutral' };
const PRIORITY_VARIANT = { LOW: 'neutral', MEDIUM: 'info', HIGH: 'warning', URGENT: 'danger' };

export function TicketsPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const list = useQuery({
    queryKey: ['sales', 'tickets', statusFilter],
    queryFn: () => salesApi.listTickets(statusFilter ? { status: statusFilter } : undefined).then((r) => r.items),
  });
  const rows = list.data || [];
  const openCount = rows.filter((r) => r.status === 'OPEN' || r.status === 'IN_PROGRESS').length;

  const columns = [
    { key: 'ticketNumber', label: t('tk.col.number'), render: (r) => <span className="mono">{r.ticketNumber}</span> },
    { key: 'customer', label: t('tk.col.customer'), render: (r) => r.customerName || '-' },
    { key: 'subject', label: t('tk.col.subject') },
    { key: 'priority', label: t('tk.col.priority'), render: (r) => <Badge variant={PRIORITY_VARIANT[r.priority]}>{t(`tk.priority.${r.priority}`)}</Badge> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{t(`tk.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('tk.action.view')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('tk.title')}</h1>
          <p className="muted">{t('tk.subtitle')}</p>
        </div>
        <div className="page-actions">
          <select className="input" style={{ maxWidth: 180 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('tk.filter.all')}</option>
            {['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'].map((s) => <option key={s} value={s}>{t(`tk.status.${s}`)}</option>)}
          </select>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('tk.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('tk.metric.total')} value={String(rows.length)} />
        <MetricCard label={t('tk.metric.open')} value={String(openCount)} trendVariant={openCount > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('tk.metric.urgent')} value={String(rows.filter((r) => r.priority === 'URGENT').length)} trendVariant="danger" />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('tk.empty')} />

      <TicketCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <TicketDetailModal ticketId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
